import React from 'react';

import RadioButton from '../../../../base/radio';
import AppButton from '../../../../base/button';
import AppInput from '../../../../base/input';
import { useFormik } from 'formik';
import { useBeneficiaryStore } from '../../../../../store';
import { useEffect } from 'react';
import beneficiarySchemas from '../../../../../constants/schemas/beneficiary';
import { accountOwnerTypeEnum } from '../../../../../models/beneficiary.model';
import GooglePlaceAutoComplete from '../../../../base/google-place-auto-complete';
import { emailRegex } from '../../../../../constants/constant';
import usePayoutStore from '../../../../../store/payout';

interface BeneficiaryPersonalInformationProps {
  goNext: () => void;
  prev: () => void;
  caller?: string;
}

const BeneficiaryPersonalInformation: React.FC<
  BeneficiaryPersonalInformationProps
> = ({ goNext, prev, caller }) => {
  const { setBeneficiaryPayload, beneficiaryPayload } = useBeneficiaryStore();
  const { beneficiaryFormState } = usePayoutStore();

  const formik = useFormik({
    initialValues: {
      accountOwnerType: 'individual',
      businessName: beneficiaryPayload.businessName ?? '',
      firstName: beneficiaryPayload.firstName ?? '',
      lastName: beneficiaryPayload.lastName ?? '',
      beneficiaryEmail: beneficiaryPayload.beneficiaryEmail ?? '',
      streetLine1: beneficiaryPayload.beneficiaryAddress.streetLine1 ?? '',
      streetLine2: beneficiaryPayload.beneficiaryAddress.streetLine2 ?? '',
      city: beneficiaryPayload.beneficiaryAddress.city ?? '',
      state: beneficiaryPayload.beneficiaryAddress.state ?? '',
      postalCode: beneficiaryPayload.beneficiaryAddress.postalCode ?? '',
      countryCode: beneficiaryPayload.beneficiaryAddress.countryCode ?? '',
      fullAddress: beneficiaryPayload.beneficiaryAddress.fullAddress ?? '',
    },
    validationSchema: beneficiarySchemas[1],
    validateOnMount: true,
    async onSubmit() {},
  });

  useEffect(() => {
    setBeneficiaryPayload({
      ...beneficiaryPayload,
      accountOwnerType:
        accountOwnerTypeEnum[
          formik.values.accountOwnerType as keyof typeof accountOwnerTypeEnum
        ],
      businessName:
        formik.values.accountOwnerType === 'business'
          ? formik.values.businessName
          : `${formik.values.firstName} ${formik.values.lastName}`,
      firstName: formik.values.firstName,
      lastName: formik.values.lastName,
      beneficiaryEmail: formik.values.beneficiaryEmail,
      beneficiaryAddress: {
        streetLine1: formik.values.streetLine1,
        streetLine2: formik.values.streetLine2,
        city: formik.values.city,
        state: formik.values.state,
        postalCode: formik.values.postalCode,
        countryCode: formik.values.countryCode,
        fullAddress: formik.values.fullAddress,
      },
    });
  }, [formik.values]);

  const accountOwnerOptions = [
    { value: 'individual', label: 'Individual' },
    { value: 'business', label: 'Business' },
  ];

  const handleAccountOwnerTypeChange = (value: string) => {
    formik.setFieldValue('accountOwnerType', value);
    formik.setFieldTouched('accountOwnerType', true);
    formik.values.accountOwnerType = value;
  };

  const disableForm = () => {
    return (
      (formik.values.accountOwnerType === 'individual' &&
        (!formik.values.firstName || !formik.values.lastName)) ||
      (formik.values.accountOwnerType === 'business' &&
        !formik.values.businessName) ||
      !formik.values.beneficiaryEmail ||
      (formik.values.beneficiaryEmail &&
        !emailRegex.test(formik.values.beneficiaryEmail)) ||
      !formik.values.streetLine1 ||
      !formik.values.city ||
      !formik.values.state ||
      !formik.values.countryCode ||
      !formik.values.postalCode ||
      !formik.values.fullAddress
    );
  };

  const submitForm = () => {
    goNext();
  };

  const handlePlaceSelected = (place: any) => {
    const addressComponents = place.address_components || [];
    let street = '';
    let route = '';
    let city = '';
    let state = '';
    let postalCode = '';
    let countryCode = '';
    const fullAddress = place.formatted_address || '';

    addressComponents.forEach((component: any) => {
      const types = component.types;
      if (types.includes('street_number')) {
        street = `${component.long_name} ${street}`;
      }
      if (types.includes('route')) {
        route = component.long_name;
      }
      if (types.includes('locality')) {
        city = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.long_name;
      }
      if (types.includes('postal_code')) {
        postalCode = component.long_name;
      }
      if (types.includes('country')) {
        countryCode = component.short_name;
      }
    });

    // Reset form fields before setting new values
    formik.setValues({
      ...formik.values,
      fullAddress,
      streetLine1: `${street} ${route}`.trim(),
      city: city || state || '',
      state: state || '',
      postalCode: postalCode || '',
      countryCode: countryCode || '',
    });
  };

  return (
    <div>
      <form
        className="flex flex-col gap-6"
        onSubmit={formik.handleSubmit}
        action="#"
      >
        <div>
          <div className="flex flex-col gap-6">
            {beneficiaryFormState !== 'selected_existing' && (
              <RadioButton
                title="Account Owner Type"
                name="transactionType"
                options={accountOwnerOptions}
                selectedValue={
                  formik.values.accountOwnerType
                    ? String(formik.values.accountOwnerType)
                    : String(beneficiaryPayload?.accountOwnerType ?? '')
                }
                onChange={handleAccountOwnerTypeChange}
              />
            )}

            <div className="grid md:grid-cols-2 grid-cols-1 gap-6">
              {beneficiaryFormState === 'selected_existing' && (
                <AppInput
                  type="text"
                  name="accountHolderName"
                  id="accountHolderName"
                  value={beneficiaryPayload.accountHolderName}
                  label="Account Holder Name"
                  disabled
                />
              )}

              {formik.values.accountOwnerType === 'business' &&
                beneficiaryFormState !== 'selected_existing' && (
                  <div className={caller === 'payout' ? '' : 'col-span-2'}>
                    <AppInput
                      type="text"
                      name="businessName"
                      id="businessName"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.businessName}
                      placeholder="Enter your business name"
                      required
                      label="Business Name"
                      errorMessage={
                        formik.touched.businessName &&
                        formik.errors.businessName
                          ? formik.errors.businessName
                          : ''
                      }
                    />
                  </div>
                )}

              {formik.values.accountOwnerType === 'individual' &&
                beneficiaryFormState !== 'selected_existing' && (
                  <AppInput
                    type="text"
                    name="firstName"
                    id="firstName"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.firstName}
                    placeholder="Enter your first name"
                    required
                    label="First Name"
                    errorMessage={
                      formik.touched.firstName && formik.errors.firstName
                        ? formik.errors.firstName
                        : ''
                    }
                  />
                )}

              {formik.values.accountOwnerType === 'individual' &&
                beneficiaryFormState !== 'selected_existing' && (
                  <AppInput
                    type="text"
                    name="lastName"
                    id="lastName"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.lastName}
                    placeholder="Enter your last name"
                    required
                    label="Last Name"
                    errorMessage={
                      formik.touched.lastName && formik.errors.lastName
                        ? formik.errors.lastName
                        : ''
                    }
                  />
                )}

              <div className={caller === 'payout' ? '' : 'col-span-2'}>
                <AppInput
                  type="text"
                  name="beneficiaryEmail"
                  id="beneficiaryEmail"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.beneficiaryEmail}
                  placeholder="Enter your email address"
                  required
                  label="Email Address"
                  errorMessage={
                    formik.touched.beneficiaryEmail &&
                    formik.errors.beneficiaryEmail
                      ? formik.errors.beneficiaryEmail
                      : ''
                  }
                  disabled={
                    caller === 'payout' &&
                    beneficiaryFormState === 'selected_existing'
                  }
                />
              </div>

              <div className="col-span-2">
                <GooglePlaceAutoComplete
                  label="Beneficiary Address"
                  rules="required"
                  id="beneficiary-location"
                  value={formik.values.fullAddress}
                  onChange={(value) =>
                    formik.setFieldValue('fullAddress', value)
                  }
                  placeholder="Enter Beneficiary Address"
                  onPlaceSelected={(place) => {
                    handlePlaceSelected(place);
                  }}
                  onBlur={async () => {
                    if (!formik.values.fullAddress) return;
                    try {
                      const geocoder = new google.maps.Geocoder();
                      const results: any = await new Promise(
                        (resolve, reject) => {
                          geocoder.geocode(
                            { address: formik.values.fullAddress },
                            (res, status) => {
                              if (status === google.maps.GeocoderStatus.OK)
                                resolve(res);
                              else reject(status);
                            },
                          );
                        },
                      );

                      if (results && results.length > 0) {
                        handlePlaceSelected(results[0]);
                      }
                    } catch (error) {
                      console.error('Geocoding failed:', error);
                    }
                  }}
                  disabled={
                    caller === 'payout' &&
                    beneficiaryFormState === 'selected_existing'
                  }
                />
              </div>
            </div>
          </div>
        </div>
        {caller !== 'payout' && (
          <div className="flex gap-4 mt-8">
            <AppButton label="Previous" type="light" clicked={prev} />
            <AppButton
              label="Next"
              type="primary"
              clicked={() => submitForm()}
              disabled={disableForm()}
            />
          </div>
        )}
      </form>
    </div>
  );
};

export default BeneficiaryPersonalInformation;
